import React from 'react'
import { Link } from 'react-router-dom'
import { useMyMembership, useMyThresholds } from '@/hooks/useMembership'
import { MembershipBadge } from '@/components/membership/MembershipBadge'
import { MembershipProgress } from '@/components/membership/MembershipProgress'

const tierOrder = ['none', 'bronze', 'silver', 'gold', 'platinum', 'vip'] as const

const tierInfo: Record<string, { label: string; icon: string; color: string; benefits: string[] }> = {
  none: {
    label: 'Peserta',
    icon: 'person',
    color: 'text-slate-500 bg-slate-100 dark:bg-white/5',
    benefits: [
      'Akses materi kursus yang diikuti',
      'Mengikuti ujian dan quiz',
      'Mendapatkan sertifikat kelulusan',
    ],
  },
  bronze: {
    label: 'Bronze',
    icon: 'workspace_premium',
    color: 'text-amber-600 bg-amber-100 dark:bg-amber-500/10',
    benefits: [
      'Semua manfaat Peserta',
      'Akses prioritas ke kursus baru',
      'Badge Bronze di profil',
    ],
  },
  silver: {
    label: 'Silver',
    icon: 'military_tech',
    color: 'text-gray-600 bg-gray-100 dark:bg-gray-400/10',
    benefits: [
      'Semua manfaat Bronze',
      'Konsultasi dengan instruktur',
      'Badge Silver di profil',
      'Akses materi eksklusif',
    ],
  },
  gold: {
    label: 'Gold',
    icon: 'emoji_events',
    color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-500/10',
    benefits: [
      'Semua manfaat Silver',
      'Prioritas dalam sesi live',
      'Badge Gold di profil',
      'Akses ke komunitas Gold',
    ],
  },
  platinum: {
    label: 'Platinum',
    icon: 'diamond',
    color: 'text-gray-700 bg-gray-200 dark:bg-gray-300/10',
    benefits: [
      'Semua manfaat Gold',
      'Mentoring 1-on-1 dengan instruktur',
      'Badge Platinum di profil',
      'Akses ke semua kursus premium',
      'Undangan ke event eksklusif',
    ],
  },
  vip: {
    label: 'VIP',
    icon: 'stars',
    color: 'text-purple-600 bg-purple-100 dark:bg-purple-500/10',
    benefits: [
      'Semua manfaat Platinum',
      'Badge VIP di profil',
      'Akses seumur hidup ke semua konten',
      'Sesi konsultasi prioritas',
      ' Merchandise eksklusif',
      'Undangan ke acara VIP tahunan',
    ],
  },
}

export const MembershipPage: React.FC = () => {
  const { data: membership, isLoading } = useMyMembership()
  const { data: thresholds } = useMyThresholds()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-24 md:pt-32 pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="space-y-6">
            <div className="h-48 bg-white dark:bg-slate-900 rounded-[2.5rem] animate-pulse border border-slate-100 dark:border-white/5" />
            <div className="h-32 bg-white dark:bg-slate-900 rounded-[2.5rem] animate-pulse border border-slate-100 dark:border-white/5" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-40 bg-white dark:bg-slate-900 rounded-[2rem] animate-pulse border border-slate-100 dark:border-white/5" />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const currentTier = membership?.tier || 'none'
  const info = tierInfo[currentTier]
  const currentIndex = tierOrder.indexOf(currentTier)
  const isMaxTier = currentTier === 'vip'

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-24 md:pt-32 pb-32">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 space-y-8">
        {/* Header with tier badge */}
        <div className="relative p-8 md:p-12 bg-slate-900 dark:bg-white rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/10">
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/20 to-transparent opacity-50" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-3">
              <p className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Membership Saya</p>
              <h1 className="text-3xl md:text-4xl font-bold text-white dark:text-slate-900 tracking-tight">
                Tier <span className="text-primary">{info?.label || 'Peserta'}</span>
              </h1>
              <p className="text-slate-400 dark:text-slate-500 text-sm font-medium max-w-lg">
                {isMaxTier
                  ? 'Selamat! Anda telah mencapai tier tertinggi. Terima kasih atas dedikasi Anda!'
                  : `Tingkatkan kehadiran Anda untuk naik ke tier berikutnya dan nikmati manfaat lebih.`}
              </p>
            </div>
            <div className="flex-shrink-0">
              <MembershipBadge tier={currentTier} size="lg" />
            </div>
          </div>
        </div>

        {/* Stats */}
        {membership && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-white/5 shadow-[0_10px_30px_rgba(0,0,0,0.02)] group hover:border-primary/20 transition-all duration-300">
              <div className="size-10 rounded-xl flex items-center justify-center mb-4 text-primary bg-primary/10 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                <span className="material-symbols-outlined text-[20px]">groups</span>
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-1">{membership.totalAttendance}</p>
              <p className="text-sm font-bold text-slate-400">Total Kehadiran</p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-white/5 shadow-[0_10px_30px_rgba(0,0,0,0.02)] group hover:border-primary/20 transition-all duration-300">
              <div className="size-10 rounded-xl flex items-center justify-center mb-4 text-green-500 bg-green-500/10 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                <span className="material-symbols-outlined text-[20px]">event_available</span>
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-1">{membership.yearAttendance}</p>
              <p className="text-sm font-bold text-slate-400">Kehadiran Tahun Ini</p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-white/5 shadow-[0_10px_30px_rgba(0,0,0,0.02)] group hover:border-primary/20 transition-all duration-300 sm:col-span-2 lg:col-span-1">
              <div className="size-10 rounded-xl flex items-center justify-center mb-4 text-amber-500 bg-amber-500/10 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                <span className="material-symbols-outlined text-[20px]">emoji_events</span>
              </div>
              <p className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight mb-1">{info?.label}</p>
              <p className="text-sm font-bold text-slate-400">Tier Saat Ini</p>
            </div>
          </div>
        )}

        {/* Progress to next tier */}
        {membership && (
          <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-white/5 p-8 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.02)]">
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-50 dark:border-white/5">
              <div className="size-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                <span className="material-symbols-outlined text-primary text-[24px]">trending_up</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white leading-none">Progres ke Tier Berikutnya</h2>
                <p className="text-sm text-slate-400 font-medium mt-1">
                  {isMaxTier ? 'Anda sudah di tier tertinggi' : `${membership.totalAttendance} dari ${thresholds ? (thresholds as any)[`membership_${tierOrder[currentIndex + 1]}_threshold`] : defaultThresholds[tierOrder[currentIndex + 1] as keyof typeof defaultThresholds]} kehadiran`}
                </p>
              </div>
            </div>
            <MembershipProgress membership={membership} thresholds={thresholds} />
          </div>
        )}

        {/* Tier Benefits */}
        <div>
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-200 dark:border-white/10">
            <div className="size-12 bg-purple-500/10 rounded-2xl flex items-center justify-center">
              <span className="material-symbols-outlined text-purple-500 text-[24px]">card_giftcard</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Manfaat Membership</h2>
              <p className="text-sm text-slate-400 font-medium mt-1">Keuntungan yang Anda dapatkan di setiap tier</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tierOrder.map((tier) => {
              const tierData = tierInfo[tier]
              const isCurrentOrPast = currentIndex >= tierOrder.indexOf(tier)
              return (
                <div
                  key={tier}
                  className={`relative bg-white dark:bg-slate-900 rounded-[2rem] border p-8 transition-all duration-300 ${
                    tier === currentTier
                      ? 'border-primary shadow-lg shadow-primary/10 ring-2 ring-primary/20'
                      : isCurrentOrPast
                        ? 'border-slate-100 dark:border-white/5 shadow-sm'
                        : 'border-slate-100 dark:border-white/5 opacity-60'
                  }`}
                >
                  {tier === currentTier && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-lg shadow-primary/30">
                      Tier Anda
                    </div>
                  )}
                  <div className={`size-12 rounded-2xl flex items-center justify-center mb-5 ${tierData.color}`}>
                    <span className="material-symbols-outlined text-[24px]">{tierData.icon}</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">{tierData.label}</h3>
                  <ul className="space-y-2.5">
                    {tierData.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 dark:text-slate-400">
                        <span className={`material-symbols-outlined text-[16px] mt-0.5 shrink-0 ${
                          isCurrentOrPast ? 'text-green-500' : 'text-slate-300 dark:text-slate-600'
                        }`}>
                          {isCurrentOrPast ? 'check_circle' : 'radio_button_unchecked'}
                        </span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>

        {/* Back to dashboard */}
        <div className="flex justify-center pt-4">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-primary transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            Kembali ke Dashboard
          </Link>
        </div>
      </div>
    </div>
  )
}

const defaultThresholds: Record<string, number> = {
  bronze: 1,
  silver: 5,
  gold: 10,
  platinum: 20,
  vip: 50,
}

export default MembershipPage
